/**
 * Web Typography Constants
 *
 * Figma 웹 타이포그래피 시스템을 기반으로 한 웹 전용 타이포그래피 상수
 * Pretendard 폰트 패밀리를 사용합니다.
 */

// Web Font Families
export const WEB_FONT_FAMILIES = {
  primary: '"Pretendard", "Roboto", "Helvetica", "Arial", sans-serif',
} as const;

// Web Font Weights
export const WEB_FONT_WEIGHTS = {
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

// Web Font Sizes (px 단위) - Figma 웹 타이포그래피 시스템 기반
export const WEB_FONT_SIZES = {
  h1: "60px", // typography/h1
  h2: "46px", // typography/h2
  h3: "30px", // typography/h3
  h4: "24px", // typography/h4
  h5: "20px", // typography/h5
  body1: "18px", // typography/body1
  body2: "16px", // typography/body2
  body3: "16px", // typography/body3
  body4: "14px", // typography/body4
  caption: "12px", // typography/caption
  button: "15px", // typography/button
} as const;

// Web Line Heights - Figma 웹 타이포그래피 시스템 기반
export const WEB_LINE_HEIGHTS = {
  h1: "116%", // typography/h1
  h2: "125%", // typography/h2
  h3: "130%", // typography/h3
  h4: "123%", // typography/h4
  h5: "140%", // typography/h5
  body1: "160%", // typography/body1
  body2: "157%", // typography/body2
  body3: "157%", // typography/body3
  body4: "157%", // typography/body4
  caption: "130%", // typography/caption
  button: "26px", // typography/button (px 단위)
} as const;

// Web Letter Spacing - Figma 웹 타이포그래피 시스템 기반
export const WEB_LETTER_SPACING = {
  h1: "0px", // typography/h1
  h2: "0px", // typography/h2
  h3: "0px", // typography/h3
  h4: "0.25px", // typography/h4
  h5: "0px", // typography/h5
  body1: "0.15px", // typography/body1
  body2: "0.1px", // typography/body2
  body3: "0.1px", // typography/body3
  body4: "0.1px", // typography/body4
  caption: "0px", // typography/caption
  button: "0.46px", // typography/button
} as const;

// Web Typography Scale - Figma 웹 타이포그래피 시스템 기반
export const WEB_TYPOGRAPHY_SCALE = {
  h1: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.bold,
    fontSize: WEB_FONT_SIZES.h1,
    lineHeight: WEB_LINE_HEIGHTS.h1,
    letterSpacing: WEB_LETTER_SPACING.h1,
  },
  h2: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.bold,
    fontSize: WEB_FONT_SIZES.h2,
    lineHeight: WEB_LINE_HEIGHTS.h2,
    letterSpacing: WEB_LETTER_SPACING.h2,
  },
  h3: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.semiBold,
    fontSize: WEB_FONT_SIZES.h3,
    lineHeight: WEB_LINE_HEIGHTS.h3,
    letterSpacing: WEB_LETTER_SPACING.h3,
  },
  h4: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.semiBold,
    fontSize: WEB_FONT_SIZES.h4,
    lineHeight: WEB_LINE_HEIGHTS.h4,
    letterSpacing: WEB_LETTER_SPACING.h4,
  },
  h5: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.medium,
    fontSize: WEB_FONT_SIZES.h5,
    lineHeight: WEB_LINE_HEIGHTS.h5,
    letterSpacing: WEB_LETTER_SPACING.h5,
  },
  body1: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.semiBold,
    fontSize: WEB_FONT_SIZES.body1,
    lineHeight: WEB_LINE_HEIGHTS.body1,
    letterSpacing: WEB_LETTER_SPACING.body1,
  },
  body2: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.medium,
    fontSize: WEB_FONT_SIZES.body2,
    lineHeight: WEB_LINE_HEIGHTS.body2,
    letterSpacing: WEB_LETTER_SPACING.body2,
  },
  body3: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.semiBold,
    fontSize: WEB_FONT_SIZES.body3,
    lineHeight: WEB_LINE_HEIGHTS.body3,
    letterSpacing: WEB_LETTER_SPACING.body3,
  },
  body4: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.medium,
    fontSize: WEB_FONT_SIZES.body4,
    lineHeight: WEB_LINE_HEIGHTS.body4,
    letterSpacing: WEB_LETTER_SPACING.body4,
  },
  caption: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.medium,
    fontSize: WEB_FONT_SIZES.caption,
    lineHeight: WEB_LINE_HEIGHTS.caption,
    letterSpacing: WEB_LETTER_SPACING.caption,
  },
  button: {
    fontFamily: WEB_FONT_FAMILIES.primary,
    fontWeight: WEB_FONT_WEIGHTS.semiBold,
    fontSize: WEB_FONT_SIZES.button,
    lineHeight: WEB_LINE_HEIGHTS.button,
    letterSpacing: WEB_LETTER_SPACING.button,
  },
} as const;

// 통합 Web Typography 객체
export const WEB_TYPOGRAPHY = {
  fontFamilies: WEB_FONT_FAMILIES,
  fontWeights: WEB_FONT_WEIGHTS,
  fontSizes: WEB_FONT_SIZES,
  lineHeights: WEB_LINE_HEIGHTS,
  letterSpacing: WEB_LETTER_SPACING,
  scale: WEB_TYPOGRAPHY_SCALE,
} as const;

// 타입 정의
export type WebFontFamilyKey = keyof typeof WEB_FONT_FAMILIES;
export type WebFontWeightKey = keyof typeof WEB_FONT_WEIGHTS;
export type WebFontSizeKey = keyof typeof WEB_FONT_SIZES;
export type WebLineHeightKey = keyof typeof WEB_LINE_HEIGHTS;
export type WebLetterSpacingKey = keyof typeof WEB_LETTER_SPACING;
export type WebTypographyScaleKey = keyof typeof WEB_TYPOGRAPHY_SCALE;
